import { useState, useCallback } from 'react';

import { FormikSelectOption } from '@models/common/FormikSelectOption';
import { ProductViewModel } from '@models/Product';
import { ProductService } from '@services/Product';

export interface UseProductDataOptions {
  onError?: (message: string) => void;
}

const emptySelectOption: FormikSelectOption[] = [{ value: '0', text: '' }];

export const useProductData = (options: UseProductDataOptions = {}) => {
  const { onError } = options;

  const [products, setProducts] =
    useState<FormikSelectOption[]>(emptySelectOption);
  const [isLoading, setIsLoading] = useState(false);

  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    const result = await ProductService.getProductSelectOptions();

    if ('data' in result) {
      const productsSelectOptions = [{ value: '0', text: '' }].concat(
        result.data.map((row) => ({
          value: row.value,
          text: row.text,
        }))
      );
      setProducts(productsSelectOptions);
    } else {
      const errorMessage = `Failed to get the product select options. ${result.errorMessages}`;
      console.error(errorMessage);
      onError?.(errorMessage);
    }

    setIsLoading(false);
  }, [onError]);

  const getProductById = useCallback(
    async (productId: number): Promise<ProductViewModel | null> => {
      const result = await ProductService.getById(productId);

      if (result.isSuccess && 'data' in result) {
        return result.data;
      } else {
        const errorMessage = `Failed to get the product details. ${result.errorMessages}`;
        console.error(errorMessage);
        onError?.(errorMessage);
        return null;
      }
    },
    [onError]
  );

  const refreshProducts = useCallback(async () => {
    await loadProducts();
  }, [loadProducts]);

  return {
    // State
    products,
    isLoading,

    // Actions
    loadProducts,
    getProductById,
    refreshProducts,

    // Setters
    setProducts,
  };
};
